import { useState, useEffect } from 'react';
import { Layout, theme } from 'antd';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import Breadcrumbs from './Breadcrumbs';
import CommandPalette from '../common/CommandPalette';
import AIAssistantDrawer from '../ai/AIAssistantDrawer';
import SkipToContent from '../common/SkipToContent';
import { layout } from '../../theme/tokens';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useNavigationShortcuts } from '../../hooks/useNavigationShortcuts';

const { Content } = Layout;

export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { token } = theme.useToken();

  // Enable keyboard shortcuts
  useKeyboardShortcuts();

  // Enable navigation shortcuts (system-wide)
  useNavigationShortcuts(true);

  // Detect mobile screen size (< 768px)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Command Palette keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const openCommandPalette = () => {
    setCommandPaletteOpen(true);
  };

  return (
    <>
      {/* Skip to Content Link - Accessibility */}
      <SkipToContent />

      <Layout style={{ minHeight: '100vh', height: '100vh', overflow: 'hidden' }}>
        <Sidebar collapsed={collapsed} />
        <Layout style={{
          marginLeft: isMobile ? 0 : (collapsed ? layout.sidebar.widthCollapsed : layout.sidebar.widthExpanded),
          transition: `margin-left ${layout.sidebar.transition}`,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden'
        }}>
          <Header collapsed={collapsed} onToggle={toggleCollapsed} onOpenCommandPalette={openCommandPalette} />
          <Content
            id="main-content"
            tabIndex={-1}
            aria-label="Main content"
            style={{
              padding: isMobile ? 16 : 24,
              background: token.colorBgContainer,
              borderRadius: 0,
              outline: 'none',
              overflow: 'auto',
              flex: 1
            }}
          >
            <Breadcrumbs />
            <Outlet />
          </Content>
        </Layout>
      </Layout>

      {/* Command Palette */}
      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
      />

      {/* AI Assistant Drawer */}
      <AIAssistantDrawer />
    </>
  );
}
